// Central catalog of FAQ sections shown across the site and aggregated on /faq.
// The Q&A *content* lives in messages/{locale}.json under each `qaNamespace`
// (q1/a1/q2/a2/... or q1/a1_p1/a1_p2/... for paragraph-format categories).
//
// SINGLE SOURCE OF TRUTH = the message files. The number of items rendered is
// NOT stored here or in any page — it is DERIVED at render time by counting the
// contiguous `qN` keys present in the namespace (see `countFaqItems`). So both
// the origin page (e.g. /contact) and the /faq aggregator read the exact same
// keys AND the exact same count. Extending or trimming an FAQ is therefore a
// single edit:
//   • add (or remove) qN + aN in all 3 messages/*.json files — nothing else.
// No page array to bump, no itemCount to keep in sync. This file only encodes
// structure that is NOT expressible in the messages: which categories exist,
// their answer format, accent colour, and originating page.

export type FaqAnswerFormat = 'single' | 'paragraphs';

export interface FaqCategory {
  id: string;
  qaNamespace: string;
  answerFormat?: FaqAnswerFormat;
  paragraphsPerAnswer?: number;
  pageHref?: string;
  accent: string;
  /**
   * Keep this category off the public /faq aggregator. Set for FAQs that belong
   * to a page which is not published yet (session-gated in middleware.ts) — the
   * aggregator is public, so listing them would leak the unpublished page's
   * content and link visitors to a 404. Flip to `false`/remove when the page
   * goes live; nothing else changes.
   */
  unlisted?: boolean;
}

export interface FaqItem {
  question: string;
  answerParagraphs: string[];
}

export const FAQ_CATEGORIES: FaqCategory[] = [
  { id: 'general',      qaNamespace: 'design.homepage.faq',     accent: '#801515', pageHref: '/' },
  { id: 'aboutUs',      qaNamespace: 'design.aboutUs.faq',      accent: '#801515', pageHref: '/about-us' },
  { id: 'spa',          qaNamespace: 'design.spa.faq',          accent: '#478B8B', pageHref: '/spa' },
  { id: 'dining',       qaNamespace: 'design.dining.faq',       accent: '#801515', pageHref: '/dining' },
  { id: 'activities',   qaNamespace: 'design.activities.faq',   accent: '#4A6741', pageHref: '/activities' },
  { id: 'corporate',    qaNamespace: 'design.corporate.faq',    accent: '#801515', pageHref: '/corporate' },
  { id: 'events',       qaNamespace: 'design.events.faq',       accent: '#801515', pageHref: '/events' },
  { id: 'newsAndOffers', qaNamespace: 'design.newsAndOffers.faq', accent: '#801515', pageHref: '/vouchers-and-offers' },
  { id: 'contact',      qaNamespace: 'design.contact.faq',      accent: '#801515', pageHref: '/contact' },
  { id: 'howToFindUs',  qaNamespace: 'design.howToFindUs.faq',  accent: '#801515', pageHref: '/how-to-find-us' },
  { id: 'newYearsEve2027', qaNamespace: 'design.newYearsEve2027.faq', accent: '#801515', pageHref: '/offers/new-years-eve-2027' },
  // Archive clone of the NYE page — admin-only (SESSION_GATED_SLUGS), so it is
  // `unlisted`: the public /faq aggregator must not surface its Q&A or link to
  // a URL that 404s for every visitor.
  { id: 'newYearsEve2027Archive', qaNamespace: 'design.newYearsEve2027Archive.faq', accent: '#801515', pageHref: '/offers/new-years-eve-2027-archive', unlisted: true },
  { id: 'autumnOffer2026', qaNamespace: 'design.autumnOffer2026.faq', answerFormat: 'paragraphs', paragraphsPerAnswer: 3, accent: '#801515', pageHref: '/offers/autumn-2026' },
  { id: 'newsletter',   qaNamespace: 'newsletterPage.faq',      accent: '#801515', pageHref: '/newsletter' },
  { id: 'rooms',        qaNamespace: 'rooms.faq',               answerFormat: 'paragraphs', paragraphsPerAnswer: 2, accent: '#801515', pageHref: '/rooms' },
  { id: 'giftVouchers', qaNamespace: 'giftVouchers.faq',        answerFormat: 'paragraphs', paragraphsPerAnswer: 2, accent: '#801515', pageHref: '/gift-vouchers' },
  { id: 'bookings',     qaNamespace: 'faqPage.bookings',        accent: '#801515' },
];

type Translator = (key: string) => string;

/**
 * Navigate a dotted namespace path (e.g. `design.contact.faq`) within a raw
 * next-intl messages tree. Returns the namespace object, or `{}` if absent.
 * Accepts `unknown` so it works with both `getMessages()` (server) and
 * `useMessages()` (client) return types.
 */
export function getFaqNamespace(messages: unknown, namespace: string): Record<string, unknown> {
  const node = namespace.split('.').reduce<unknown>(
    (current, key) =>
      current && typeof current === 'object'
        ? (current as Record<string, unknown>)[key]
        : undefined,
    messages,
  );
  return node && typeof node === 'object' ? (node as Record<string, unknown>) : {};
}

/**
 * Count the FAQ items in a namespace by walking `q1`, `q2`, … until the first
 * gap. This is the ONE count that drives every FAQ surface — deriving it from
 * the messages is what keeps origin pages and /faq in sync automatically.
 */
export function countFaqItems(namespaceMessages: Record<string, unknown>): number {
  let count = 0;
  while (namespaceMessages[`q${count + 1}`] !== undefined) count += 1;
  return count;
}

export function buildFaqItems(t: Translator, category: FaqCategory, itemCount: number): FaqItem[] {
  return Array.from({ length: itemCount }, (_, idx) => {
    const i = idx + 1;
    const question = t(`q${i}`);
    const answerParagraphs = category.answerFormat === 'paragraphs'
      ? Array.from(
          { length: category.paragraphsPerAnswer ?? 2 },
          (_, j) => t(`a${i}_p${j + 1}`)
        )
      : [t(`a${i}`)];
    return { question, answerParagraphs };
  });
}
